import { useMemo, useState } from "react";
import { useLocation } from "wouter";
import { Eye, Pencil, Plus, Search, Trash2, UserPlus, X, ShieldAlert } from "lucide-react";
import { AppLayout } from "@/components/layout/AppLayout";
import { Badge, Card, PrimaryButton, SectionTitle } from "@/components/Card";
import { useDemoStore, roleLabels, roleHomes, type Role } from "@/store/useDemoStore";
import { useUsersList } from "@/store/hooks";
import type { AppUser } from "@/data/seed";

type RoleFilter = "todos" | Role;
type EstadoFilter = "todos" | "activo" | "inactivo";

const ALL_ROLES: Role[] = ["admin", "alumno", "evaluador", "dictaminador", "instructor", "auditor"];

function initialsFromName(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "??";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

function genId(): string {
  return `usr-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 6)}`;
}

interface FormState {
  id: string | null;
  nombre: string;
  email: string;
  curp: string;
  cedula: string;
  foto: string;
  roles: Role[];
  estado: "activo" | "inactivo";
  capacidad: string;
  notas: string;
}

const emptyForm: FormState = {
  id: null,
  nombre: "",
  email: "",
  curp: "",
  cedula: "",
  foto: "",
  roles: ["alumno"],
  estado: "activo",
  capacidad: "12",
  notas: "",
};

function userToForm(u: AppUser): FormState {
  return {
    id: u.id,
    nombre: u.nombre,
    email: u.email,
    curp: u.curp ?? "",
    cedula: u.cedula ?? "",
    foto: u.foto ?? "",
    roles: u.roles.length > 0 ? u.roles : [u.rol],
    estado: u.estado,
    capacidad: typeof u.capacidad === "number" ? String(u.capacidad) : "12",
    notas: u.notas ?? "",
  };
}

export default function AdminUsersPage() {
  const users = useUsersList();
  const addUser = useDemoStore((s) => s.addUser);
  const updateUser = useDemoStore((s) => s.updateUser);
  const removeUser = useDemoStore((s) => s.removeUser);
  const setRole = useDemoStore((s) => s.setRole);
  const setEffectiveUser = useDemoStore((s) => s.setEffectiveUser);
  const effectiveUserId = useDemoStore((s) => s.settings.effectiveUserId);
  const activeRole = useDemoStore((s) => s.activeRole);
  const logEvent = useDemoStore((s) => s.logEvent);
  const [, setLocation] = useLocation();

  const [query, setQuery] = useState("");
  const [roleFilter, setRoleFilter] = useState<RoleFilter>("todos");
  const [estadoFilter, setEstadoFilter] = useState<EstadoFilter>("todos");
  const [editorOpen, setEditorOpen] = useState(false);
  const [form, setForm] = useState<FormState>(emptyForm);
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});

  const sorted = useMemo(
    () => [...users].sort((a, b) => a.nombre.localeCompare(b.nombre, "es")),
    [users],
  );

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return sorted.filter((u) => {
      if (roleFilter !== "todos" && !u.roles.includes(roleFilter)) return false;
      if (estadoFilter !== "todos" && u.estado !== estadoFilter) return false;
      if (!q) return true;
      return (
        u.nombre.toLowerCase().includes(q) ||
        u.email.toLowerCase().includes(q) ||
        (u.curp ?? "").toLowerCase().includes(q) ||
        (u.cedula ?? "").toLowerCase().includes(q)
      );
    });
  }, [sorted, query, roleFilter, estadoFilter]);

  const countsByRole = useMemo(() => {
    const counts: Record<Role, { total: number; activos: number }> = {
      admin: { total: 0, activos: 0 },
      alumno: { total: 0, activos: 0 },
      evaluador: { total: 0, activos: 0 },
      dictaminador: { total: 0, activos: 0 },
      instructor: { total: 0, activos: 0 },
      auditor: { total: 0, activos: 0 },
    };
    for (const u of users) {
      for (const r of u.roles) {
        counts[r].total += 1;
        if (u.estado === "activo") counts[r].activos += 1;
      }
    }
    return counts;
  }, [users]);

  const activeAdminsCount = useMemo(
    () => users.filter((u) => u.roles.includes("admin") && u.estado === "activo").length,
    [users],
  );

  function openCreate() {
    setForm(emptyForm);
    setFormErrors({});
    setEditorOpen(true);
  }
  function openEdit(u: AppUser) {
    setForm(userToForm(u));
    setFormErrors({});
    setEditorOpen(true);
  }
  function closeEditor() {
    setEditorOpen(false);
    setFormErrors({});
  }

  function toggleRole(r: Role) {
    setForm((f) => {
      const has = f.roles.includes(r);
      const next = has ? f.roles.filter((x) => x !== r) : [...f.roles, r];
      return { ...f, roles: next };
    });
  }

  function validate(): { ok: boolean; errors: Record<string, string> } {
    const errors: Record<string, string> = {};
    const nombre = form.nombre.trim();
    const email = form.email.trim().toLowerCase();
    if (!nombre) errors.nombre = "El nombre es obligatorio.";
    if (!email) errors.email = "El correo es obligatorio.";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) errors.email = "Formato de correo inválido.";
    else {
      const conflict = users.find((u) => u.email.toLowerCase() === email && u.id !== form.id);
      if (conflict) errors.email = `Ya existe otro usuario con este correo (${conflict.nombre}).`;
    }
    if (form.roles.length === 0) errors.roles = "Asigna al menos un rol.";
    if (form.roles.includes("evaluador")) {
      const cap = Number(form.capacidad);
      if (!Number.isFinite(cap) || cap <= 0 || cap > 200) {
        errors.capacidad = "Capacidad debe ser un número entre 1 y 200.";
      }
    }
    // At least one active admin invariant
    if (form.id) {
      const original = users.find((u) => u.id === form.id);
      const wasActiveAdmin = original && original.roles.includes("admin") && original.estado === "activo";
      const willBeActiveAdmin = form.roles.includes("admin") && form.estado === "activo";
      if (wasActiveAdmin && !willBeActiveAdmin && activeAdminsCount <= 1) {
        errors.roles = "Debe existir al menos un administrador activo. No puedes desactivar o quitar el rol admin del último administrador.";
      }
    }
    return { ok: Object.keys(errors).length === 0, errors };
  }

  function handleSave() {
    const { ok, errors } = validate();
    if (!ok) {
      setFormErrors(errors);
      return;
    }
    const id = form.id ?? genId();
    const iniciales = initialsFromName(form.nombre);
    const isEvaluador = form.roles.includes("evaluador");
    const user: AppUser = {
      id,
      nombre: form.nombre.trim(),
      email: form.email.trim().toLowerCase(),
      curp: form.curp.trim() || undefined,
      cedula: form.cedula.trim() || undefined,
      foto: form.foto.trim() || undefined,
      rol: form.roles[0],
      roles: form.roles,
      estado: form.estado,
      iniciales,
      capacidad: isEvaluador ? Number(form.capacidad) : undefined,
      notas: form.notas.trim() || undefined,
    };
    if (form.id) {
      updateUser(form.id, user);
      logEvent({ actor: "Administrador académico", rol: activeRole, accion: "actualizar", entidad: "user", entidadId: id });
    } else {
      addUser(user);
      logEvent({ actor: "Administrador académico", rol: activeRole, accion: "crear", entidad: "user", entidadId: id });
    }
    setEditorOpen(false);
  }

  function handleToggleActive(u: AppUser) {
    const isLastActiveAdmin =
      u.roles.includes("admin") && u.estado === "activo" && activeAdminsCount <= 1;
    if (u.estado === "activo" && isLastActiveAdmin) {
      window.alert("No puedes desactivar al último administrador activo del sistema.");
      return;
    }
    const next = u.estado === "activo" ? "inactivo" : "activo";
    updateUser(u.id, { estado: next });
    logEvent({
      actor: "Administrador académico",
      rol: activeRole,
      accion: next === "activo" ? "reactivar" : "desactivar",
      entidad: "user",
      entidadId: u.id,
    });
  }

  function handleDelete(u: AppUser) {
    if (u.roles.includes("admin") && u.estado === "activo" && activeAdminsCount <= 1) {
      window.alert("No puedes eliminar al último administrador activo del sistema.");
      return;
    }
    if (!window.confirm(`¿Eliminar al usuario "${u.nombre}"? Esta acción no se puede deshacer en la demo.`)) return;
    removeUser(u.id);
    if (effectiveUserId === u.id) setEffectiveUser(null);
    logEvent({ actor: "Administrador académico", rol: activeRole, accion: "eliminar", entidad: "user", entidadId: u.id });
  }

  function handleViewAs(u: AppUser) {
    if (u.estado !== "activo") {
      window.alert("Solo puedes entrar como un usuario activo.");
      return;
    }
    const r: Role = u.roles[0];
    setEffectiveUser(u.id);
    setRole(r);
    logEvent({ actor: "Administrador académico", rol: activeRole, accion: "ver-como", entidad: "user", entidadId: u.id });
    setLocation(roleHomes[r]);
  }

  return (
    <AppLayout pageTitle="Usuarios y roles" breadcrumb={["Admin", "Usuarios y roles"]}>
      <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
        {/* Summary cards */}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(6, minmax(0, 1fr))", gap: 12 }}>
          {ALL_ROLES.map((r) => (
            <Card key={r}>
              <div style={{ fontSize: 11, color: "var(--cc-gray-500)", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.05em" }}>
                {roleLabels[r]}
              </div>
              <div style={{ display: "flex", alignItems: "baseline", gap: 6, marginTop: 6 }}>
                <span style={{ fontSize: 22, fontWeight: 700, color: "var(--cc-navy)" }}>
                  {countsByRole[r].activos}
                </span>
                <span style={{ fontSize: 12, color: "var(--cc-gray-500)" }}>
                  / {countsByRole[r].total} activos
                </span>
              </div>
            </Card>
          ))}
        </div>

        {/* Toolbar */}
        <Card>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
              <div style={{ position: "relative" }}>
                <Search
                  size={14}
                  style={{ position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)", color: "var(--cc-gray-400)" }}
                />
                <input
                  data-testid="input-users-search"
                  type="text"
                  placeholder="Buscar por nombre, correo, CURP…"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  style={{
                    padding: "8px 12px 8px 30px",
                    border: "1px solid var(--cc-gray-200)",
                    borderRadius: 8,
                    fontSize: 13,
                    width: 280,
                    background: "white",
                  }}
                />
              </div>
              <select
                data-testid="select-role-filter"
                value={roleFilter}
                onChange={(e) => setRoleFilter(e.target.value as RoleFilter)}
                style={selectStyle}
              >
                <option value="todos">Todos los roles</option>
                {ALL_ROLES.map((r) => (
                  <option key={r} value={r}>{roleLabels[r]}</option>
                ))}
              </select>
              <select
                data-testid="select-estado-filter"
                value={estadoFilter}
                onChange={(e) => setEstadoFilter(e.target.value as EstadoFilter)}
                style={selectStyle}
              >
                <option value="todos">Todos los estados</option>
                <option value="activo">Activos</option>
                <option value="inactivo">Inactivos</option>
              </select>
              <span style={{ fontSize: 12, color: "var(--cc-gray-500)" }}>
                {filtered.length} de {users.length}
              </span>
            </div>
            <PrimaryButton onClick={openCreate} testId="button-new-user">
              <Plus size={14} />
              Nuevo usuario
            </PrimaryButton>
          </div>
        </Card>

        {/* User list */}
        <Card>
          <SectionTitle>Usuarios registrados</SectionTitle>
          {filtered.length === 0 ? (
            <div style={{ padding: 24, textAlign: "center", color: "var(--cc-gray-500)", fontSize: 13 }}>
              No hay usuarios con esos filtros.
            </div>
          ) : (
            <div style={{ overflowX: "auto", marginTop: 8 }}>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                <thead>
                  <tr style={{ background: "var(--cc-gray-50)", borderBottom: "1px solid var(--cc-gray-200)" }}>
                    <th style={thStyle}>Usuario</th>
                    <th style={thStyle}>Correo</th>
                    <th style={thStyle}>Roles</th>
                    <th style={thStyle}>Estado</th>
                    <th style={thStyle}>Capacidad</th>
                    <th style={{ ...thStyle, textAlign: "right" }}>Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((u) => (
                    <tr key={u.id} style={{ borderBottom: "1px solid var(--cc-gray-100)" }} data-testid={`row-user-${u.id}`}>
                      <td style={tdStyle}>
                        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                          {u.foto ? (
                            <img
                              src={u.foto}
                              alt=""
                              style={{ width: 32, height: 32, borderRadius: "50%", objectFit: "cover", border: "1px solid var(--cc-gray-200)" }}
                            />
                          ) : (
                            <div
                              style={{
                                width: 32, height: 32, borderRadius: "50%",
                                background: "var(--cc-navy)", color: "white",
                                display: "flex", alignItems: "center", justifyContent: "center",
                                fontSize: 11, fontWeight: 700,
                              }}
                            >
                              {u.iniciales}
                            </div>
                          )}
                          <div style={{ minWidth: 0 }}>
                            <div style={{ fontWeight: 600, color: "var(--cc-gray-800)" }}>{u.nombre}</div>
                            {u.cedula && (
                              <div style={{ fontSize: 11, color: "var(--cc-gray-500)" }}>Céd. {u.cedula}</div>
                            )}
                          </div>
                        </div>
                      </td>
                      <td style={{ ...tdStyle, color: "var(--cc-gray-700)" }}>{u.email}</td>
                      <td style={tdStyle}>
                        <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
                          {u.roles.map((r) => (
                            <Badge key={r} tone="navy">{roleLabels[r]}</Badge>
                          ))}
                        </div>
                      </td>
                      <td style={tdStyle}>
                        <Badge tone={u.estado === "activo" ? "green" : "gray"}>
                          {u.estado === "activo" ? "Activo" : "Inactivo"}
                        </Badge>
                      </td>
                      <td style={{ ...tdStyle, color: "var(--cc-gray-700)" }}>
                        {u.roles.includes("evaluador") && typeof u.capacidad === "number"
                          ? `${u.capacidad} cands.`
                          : "—"}
                      </td>
                      <td style={{ ...tdStyle, textAlign: "right" }}>
                        <div style={{ display: "inline-flex", gap: 6 }}>
                          <button
                            onClick={() => handleViewAs(u)}
                            disabled={u.estado !== "activo"}
                            title="Ver como este usuario"
                            data-testid={`button-view-as-${u.id}`}
                            style={iconBtnStyle(u.estado !== "activo")}
                          >
                            <Eye size={14} />
                          </button>
                          <button
                            onClick={() => handleToggleActive(u)}
                            title={u.estado === "activo" ? "Desactivar" : "Reactivar"}
                            data-testid={`button-toggle-${u.id}`}
                            style={iconBtnStyle(false)}
                          >
                            <ShieldAlert size={14} />
                          </button>
                          <button
                            onClick={() => openEdit(u)}
                            title="Editar"
                            data-testid={`button-edit-${u.id}`}
                            style={iconBtnStyle(false)}
                          >
                            <Pencil size={14} />
                          </button>
                          <button
                            onClick={() => handleDelete(u)}
                            title="Eliminar"
                            data-testid={`button-delete-${u.id}`}
                            style={{ ...iconBtnStyle(false), color: "var(--cc-red)" }}
                          >
                            <Trash2 size={14} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      </div>

      {editorOpen && (
        <UserEditor
          form={form}
          setForm={setForm}
          errors={formErrors}
          isEdit={!!form.id}
          onClose={closeEditor}
          onSave={handleSave}
          onToggleRole={toggleRole}
        />
      )}
    </AppLayout>
  );
}

const selectStyle: React.CSSProperties = {
  padding: "8px 10px",
  border: "1px solid var(--cc-gray-200)",
  borderRadius: 8,
  fontSize: 13,
  background: "white",
  color: "var(--cc-gray-800)",
  cursor: "pointer",
};

const thStyle: React.CSSProperties = {
  padding: "10px 12px",
  textAlign: "left",
  fontSize: 11,
  fontWeight: 700,
  color: "var(--cc-gray-600)",
  textTransform: "uppercase",
  letterSpacing: "0.05em",
};
const tdStyle: React.CSSProperties = { padding: "10px 12px", verticalAlign: "middle" };

function iconBtnStyle(disabled: boolean): React.CSSProperties {
  return {
    width: 28, height: 28, borderRadius: 6,
    border: "1px solid var(--cc-gray-200)",
    background: "white",
    cursor: disabled ? "not-allowed" : "pointer",
    display: "inline-flex", alignItems: "center", justifyContent: "center",
    color: "var(--cc-gray-600)",
    opacity: disabled ? 0.4 : 1,
  };
}

interface UserEditorProps {
  form: FormState;
  setForm: React.Dispatch<React.SetStateAction<FormState>>;
  errors: Record<string, string>;
  isEdit: boolean;
  onClose: () => void;
  onSave: () => void;
  onToggleRole: (r: Role) => void;
}

function UserEditor({ form, setForm, errors, isEdit, onClose, onSave, onToggleRole }: UserEditorProps) {
  return (
    <div
      role="dialog"
      aria-modal="true"
      onClick={onClose}
      style={{
        position: "fixed", inset: 0, background: "rgba(15, 23, 42, 0.5)",
        display: "flex", alignItems: "center", justifyContent: "center", zIndex: 50,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: "white", borderRadius: 12, width: "min(640px, 92vw)",
          maxHeight: "90vh", overflow: "auto", boxShadow: "0 20px 50px rgba(0,0,0,0.25)",
        }}
        data-testid="dialog-user-editor"
      >
        <div style={{
          display: "flex", alignItems: "center", justifyContent: "space-between",
          padding: "16px 20px", borderBottom: "1px solid var(--cc-gray-200)",
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <UserPlus size={18} color="var(--cc-navy)" />
            <h2 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: "var(--cc-navy)" }}>
              {isEdit ? "Editar usuario" : "Nuevo usuario"}
            </h2>
          </div>
          <button
            onClick={onClose}
            data-testid="button-close-editor"
            style={{
              width: 30, height: 30, borderRadius: 6, border: "1px solid var(--cc-gray-200)",
              background: "white", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center",
            }}
          >
            <X size={14} />
          </button>
        </div>

        <div style={{ padding: 20, display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
          <Field label="Nombre completo *" error={errors.nombre} full>
            <input
              data-testid="input-user-nombre"
              value={form.nombre}
              onChange={(e) => setForm((f) => ({ ...f, nombre: e.target.value }))}
              style={inputStyle}
              placeholder="p. ej. Mtra. Alicia Aguirre"
            />
          </Field>
          <Field label="Correo electrónico *" error={errors.email}>
            <input
              data-testid="input-user-email"
              type="email"
              value={form.email}
              onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
              style={inputStyle}
              placeholder="usuario@certicore.mx"
            />
          </Field>
          <Field label="CURP" error={errors.curp}>
            <input
              data-testid="input-user-curp"
              value={form.curp}
              onChange={(e) => setForm((f) => ({ ...f, curp: e.target.value.toUpperCase() }))}
              style={inputStyle}
              placeholder="Opcional"
              maxLength={18}
            />
          </Field>
          <Field label="Cédula / matrícula">
            <input
              data-testid="input-user-cedula"
              value={form.cedula}
              onChange={(e) => setForm((f) => ({ ...f, cedula: e.target.value }))}
              style={inputStyle}
              placeholder="Opcional"
            />
          </Field>
          <Field label="Foto (URL)">
            <input
              data-testid="input-user-foto"
              value={form.foto}
              onChange={(e) => setForm((f) => ({ ...f, foto: e.target.value }))}
              style={inputStyle}
              placeholder="https://…"
            />
          </Field>
          <Field label="Roles *" error={errors.roles} full>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
              {ALL_ROLES.map((r) => {
                const active = form.roles.includes(r);
                return (
                  <button
                    key={r}
                    type="button"
                    onClick={() => onToggleRole(r)}
                    data-testid={`chip-role-${r}`}
                    style={{
                      padding: "6px 12px", borderRadius: 999,
                      border: active ? "1px solid var(--cc-navy)" : "1px solid var(--cc-gray-200)",
                      background: active ? "var(--cc-navy)" : "white",
                      color: active ? "white" : "var(--cc-gray-700)",
                      fontSize: 12, fontWeight: 600, cursor: "pointer",
                    }}
                  >
                    {roleLabels[r]}
                  </button>
                );
              })}
            </div>
          </Field>
          <Field label="Estado">
            <select
              data-testid="select-user-estado"
              value={form.estado}
              onChange={(e) => setForm((f) => ({ ...f, estado: e.target.value as "activo" | "inactivo" }))}
              style={inputStyle}
            >
              <option value="activo">Activo</option>
              <option value="inactivo">Inactivo</option>
            </select>
          </Field>
          {form.roles.includes("evaluador") && (
            <Field label="Capacidad simultánea (candidatos)" error={errors.capacidad}>
              <input
                data-testid="input-user-capacidad"
                type="number"
                min={1}
                max={200}
                value={form.capacidad}
                onChange={(e) => setForm((f) => ({ ...f, capacidad: e.target.value }))}
                style={inputStyle}
              />
            </Field>
          )}
          <Field label="Notas" full>
            <textarea
              data-testid="textarea-user-notas"
              value={form.notas}
              onChange={(e) => setForm((f) => ({ ...f, notas: e.target.value }))}
              rows={2}
              style={{ ...inputStyle, fontFamily: "inherit", resize: "vertical" }}
            />
          </Field>
        </div>

        <div style={{
          display: "flex", justifyContent: "flex-end", gap: 8,
          padding: "12px 20px", borderTop: "1px solid var(--cc-gray-200)", background: "var(--cc-gray-50)",
        }}>
          <button
            onClick={onClose}
            data-testid="button-cancel-editor"
            style={{
              padding: "8px 14px", borderRadius: 8, border: "1px solid var(--cc-gray-200)",
              background: "white", fontSize: 13, fontWeight: 600, cursor: "pointer", color: "var(--cc-gray-700)",
            }}
          >
            Cancelar
          </button>
          <PrimaryButton onClick={onSave} testId="button-save-user">
            {isEdit ? "Guardar cambios" : "Crear usuario"}
          </PrimaryButton>
        </div>
      </div>
    </div>
  );
}

const inputStyle: React.CSSProperties = {
  width: "100%",
  padding: "8px 10px",
  border: "1px solid var(--cc-gray-200)",
  borderRadius: 8,
  fontSize: 13,
  background: "white",
  color: "var(--cc-gray-800)",
};

function Field({
  label, error, full, children,
}: { label: string; error?: string; full?: boolean; children: React.ReactNode }) {
  return (
    <div style={{ gridColumn: full ? "1 / -1" : undefined, display: "flex", flexDirection: "column", gap: 4 }}>
      <label style={{ fontSize: 11, fontWeight: 700, color: "var(--cc-gray-600)", textTransform: "uppercase", letterSpacing: "0.05em" }}>
        {label}
      </label>
      {children}
      {error && <span style={{ fontSize: 11, color: "var(--cc-red)" }}>{error}</span>}
    </div>
  );
}
